import { motion } from 'framer-motion';
import { MessageSquare, ClipboardList, Cog, CheckCircle2 } from 'lucide-react';

const steps = [
  {
    number: '01',
    icon: MessageSquare,
    title: 'Consultation',
    description: 'Initial discussion to understand your specific needs, timeline, and project requirements.',
  },
  {
    number: '02',
    icon: ClipboardList,
    title: 'Requirement Assessment',
    description: 'Detailed analysis of scope, deliverables, and resource allocation for your project.',
  },
  {
    number: '03',
    icon: Cog,
    title: 'Execution',
    description: 'Our team executes the work with precision, maintaining regular communication throughout.',
  },
  {
    number: '04',
    icon: CheckCircle2,
    title: 'Delivery & Review',
    description: 'Quality-checked deliverables with opportunity for revisions and feedback integration.',
  },
];

export const ProcessSection = () => {
  return (
    <section
      id="process"
      className="py-24 md:py-32 relative overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, rgba(10,25,47,0.04) 0%, #fdfdfc 40%, rgba(217,48,37,0.05) 100%)'
      }}
      data-testid="process-section"
    >
      {/* Gradient decorations */}
      <div className="absolute top-1/2 left-0 w-64 h-64 bg-[#0a192f]/5 rounded-full blur-3xl -translate-y-1/2" />
      <div className="absolute top-1/2 right-0 w-64 h-64 bg-[#D93025]/5 rounded-full blur-3xl -translate-y-1/2" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-xs font-bold uppercase tracking-widest text-[#D93025]">
            Our Process
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-[#0a192f] mt-4 mb-6">
            How We Work
          </h2>
          <p className="text-base md:text-lg text-[#64748b] max-w-2xl mx-auto">
            A streamlined, transparent process designed to deliver exceptional results 
            while keeping you informed at every step.
          </p>
        </motion.div>

        {/* Process Steps */}
        <div className="process-grid">
          {steps.map((step, index) => (
            <motion.div
              key={step.number}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15, duration: 0.5 }}
              className={`relative ${index < steps.length - 1 ? 'process-connector' : ''}`}
              data-testid={`process-step-${index}`}
            >
              <div className="bg-white rounded-xl border border-[#e2e2dc] p-8 shadow-sm hover:shadow-md transition-all duration-300 h-full relative">
                {/* Step Number */}
                <div className="absolute -top-4 left-8">
                  <span className="bg-[#0a192f] text-white text-xs font-bold px-3 py-1.5 rounded-full">
                    Step {step.number}
                  </span>
                </div>

                {/* Icon */}
                <div className="w-16 h-16 bg-[#f5f5f0] rounded-2xl flex items-center justify-center mb-6 mt-2">
                  <step.icon className="h-8 w-8 text-[#0a192f]" />
                </div>

                {/* Content */}
                <h3 className="font-heading text-xl font-medium text-[#0a192f] mb-3">
                  {step.title}
                </h3>
                <p className="text-sm text-[#64748b] leading-relaxed">
                  {step.description}
                </p>

                {/* Connector Dot for Mobile */}
                {index < steps.length - 1 && (
                  <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 md:hidden">
                    <div className="w-2 h-2 bg-[#0a192f] rounded-full" />
                    <div className="w-0.5 h-6 bg-[#0a192f]/30 mx-auto" />
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Horizontal Timeline (Desktop Only) */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="hidden md:flex justify-between items-center mt-8 px-16"
        >
          {steps.map((step, index) => (
            <div key={`dot-${step.number}`} className="flex items-center">
              <div className="w-4 h-4 bg-[#0a192f] rounded-full relative">
                <div className="absolute inset-0 bg-[#0a192f] rounded-full animate-ping opacity-20" />
              </div>
              {index < steps.length - 1 && (
                <div className="w-full h-0.5 bg-gradient-to-r from-[#0a192f] to-[#0a192f]/20 min-w-[80px]" />
              )}
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
